// Auth utility functions

export const AUTH_TOKEN_EVENT = "auth-token-changed";
export const WORKOS_AUTH_STATE_KEY = "workos_auth_state";
export const WORKOS_AUTH_RETURN_TO_KEY = "workos_auth_return_to";
export const WORKOS_AUTH_REMEMBER_KEY = "workos_auth_remember";
const AUTH_LAST_ACTIVITY_KEY = "auth_last_activity_at";
const LEGACY_RUNBOOKS_CACHE_KEY = "bitsentry_runbooks";

// Default remember me expiry fallback: 2 weeks (in hours)
const DEFAULT_REMEMBER_ME_EXPIRY_HOURS = 14 * 24;

const notifyTokenChange = () => {
  window.dispatchEvent(new Event(AUTH_TOKEN_EVENT));
};

const isTokenExpired = (): boolean => {
  const expiryStr = localStorage.getItem("token_expiry");
  if (expiryStr === null || expiryStr === "") {
    return false;
  }
  const expiry = parseInt(expiryStr, 10);
  return Date.now() > expiry;
};

export const isAuthenticated = (): boolean => {
  // Check localStorage token with expiry
  const localToken = localStorage.getItem("token");
  if (localToken !== null && localToken !== "") {
    if (isTokenExpired()) {
      clearTokens();
      return false;
    }
    return true;
  }
  // Session storage tokens don't have expiry (cleared on browser close)
  const sessionToken = sessionStorage.getItem("token");
  return sessionToken !== null && sessionToken !== "";
};

export const getToken = (): string | null => {
  // Check localStorage token with expiry
  const localToken = localStorage.getItem("token");
  if (localToken !== null && localToken !== "") {
    if (isTokenExpired()) {
      clearTokens();
      return null;
    }
    return localToken;
  }
  return sessionStorage.getItem("token");
};

export const getRefreshToken = (): string | null => {
  const localRefreshToken = localStorage.getItem("refreshToken");
  if (localRefreshToken !== null && localRefreshToken !== "") {
    return localRefreshToken;
  }

  return sessionStorage.getItem("refreshToken");
};

export const usesPersistentAuthStorage = (): boolean => {
  return (
    localStorage.getItem("refreshToken") !== null ||
    localStorage.getItem("token") !== null
  );
};

function resolveActivityStorage(): Storage {
  if (usesPersistentAuthStorage()) {
    return localStorage;
  }

  return sessionStorage;
}

export const setToken = (token: string, remember = false): void => {
  if (remember) {
    localStorage.setItem("token", token);
    const expiry =
      Date.now() + DEFAULT_REMEMBER_ME_EXPIRY_HOURS * 60 * 60 * 1000;
    localStorage.setItem("token_expiry", expiry.toString());
  } else {
    sessionStorage.setItem("token", token);
  }
  notifyTokenChange();
};

export const setRefreshToken = (
  refreshToken: string,
  remember = false,
): void => {
  if (remember) {
    localStorage.setItem("refreshToken", refreshToken);
  } else {
    sessionStorage.setItem("refreshToken", refreshToken);
  }
};

export const replaceToken = (token: string): void => {
  if (usesPersistentAuthStorage()) {
    localStorage.setItem("token", token);
  } else {
    sessionStorage.setItem("token", token);
  }
  notifyTokenChange();
};

export const replaceRefreshToken = (refreshToken: string): void => {
  if (usesPersistentAuthStorage()) {
    localStorage.setItem("refreshToken", refreshToken);
  } else {
    sessionStorage.setItem("refreshToken", refreshToken);
  }
};

export const getLastActivityAt = (): number | null => {
  const storedValue = resolveActivityStorage().getItem(AUTH_LAST_ACTIVITY_KEY);
  if (storedValue === null || storedValue === "") {
    return null;
  }

  const parsedValue = Number.parseInt(storedValue, 10);
  if (Number.isFinite(parsedValue)) {
    return parsedValue;
  }

  return null;
};

export const setLastActivityAt = (timestamp: number): void => {
  const storage = resolveActivityStorage();
  storage.setItem(AUTH_LAST_ACTIVITY_KEY, timestamp.toString());

  let alternateStorage = localStorage;
  if (storage === localStorage) {
    alternateStorage = sessionStorage;
  }
  alternateStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
};

export const clearTokens = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("token_expiry");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
  localStorage.removeItem(LEGACY_RUNBOOKS_CACHE_KEY);
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);

  notifyTokenChange();
};
